import type { InMemoryCacheConfig, NormalizedCacheObject } from "@apollo/client";
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { reportError } from "@phoenix/helpers/reportError";

/**
 * Function creates a graphql client
 * @param uri                                 - uri of the graphql server
 * @param inMemoryCacheConfig                 - configurations to pass to the inMemoryCache
 * @returns ApolloClient
 */
const graphClient = (uri?: string, inMemoryCacheConfig?: InMemoryCacheConfig): ApolloClient<NormalizedCacheObject> => {
  const authLink = setContext((_, { headers }) => {
    const token = localStorage.getItem("token");
    const bearer = JSON.parse(token ?? "")?.value ?? "";
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${bearer}` : "",
      },
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map((graphqlError) => {
        const { message, locations, path } = graphqlError;
        reportError(new Error(`GraphQL Error: ${message}, ${locations}, ${path}`, { cause: graphqlError }));
      });
      return;
    }
    if (networkError) {
      console.warn(`Network Error: ${networkError.message}`);
    }
  });
  const httpLink = new HttpLink({ uri });
  const link: ApolloLink = ApolloLink.from([errorLink, authLink, httpLink]);

  return new ApolloClient({
    uri,
    link: link as unknown as ApolloLink,
    cache: new InMemoryCache(inMemoryCacheConfig),
    connectToDevTools: process.env.NODE_ENV !== "production",
    queryDeduplication: true,
  });
};

export { graphClient };
